import { useState } from "react";
import {
  Paper,
  Group,
  Stack,
  Title,
  Text,
  Button,
  Badge,
  Alert,
  Code,
  Divider,
  List,
  ThemeIcon,
  Anchor,
  Loader,
  Checkbox,
  Tooltip,
} from "@mantine/core";
import {
  IconCloudUpload,
  IconArrowLeft,
  IconArrowRight,
  IconAlertTriangle,
  IconKey,
  IconShieldCheck,
  IconCheck,
  IconCircleX,
  IconRefresh,
  IconFile,
  IconPlayerPlay,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { grInvoiceAutomationApi } from "../../../api/grInvoiceAutomation.api";
import { apiError } from "../../../api/client";

export function DuvoPushPanel({ payload, totalCount, onBack, onReset }) {
  const [pushResult, setPushResult] = useState(null);
  const [pushError, setPushError] = useState(null);
  const [sandboxFiles, setSandboxFiles] = useState(null);
  const [startRun, setStartRun] = useState(false);

  const { data: configStatus, isLoading: configLoading } = useQuery({
    queryKey: ["gr-invoice-automation", "config-status"],
    queryFn: grInvoiceAutomationApi.configStatus,
  });

  const canPush =
    configStatus?.apiKey && configStatus?.assignmentID && payload.length > 0;

  const pushMut = useMutation({
    mutationFn: () => grInvoiceAutomationApi.duvoPush(payload, { startRun }),
    onMutate: () => {
      setPushError(null);
      setPushResult(null);
      setSandboxFiles(null);
    },
    onSuccess: (data) => setPushResult(data),
    onError: (e) => setPushError(apiError(e)),
  });

  const verifyMut = useMutation({
    mutationFn: () =>
      grInvoiceAutomationApi.sandboxFiles(pushResult.sandbox_id),
    onSuccess: (files) => setSandboxFiles(files),
    onError: (e) => setSandboxFiles({ error: apiError(e) }),
  });

  return (
    <Stack gap="md">
      <Paper>
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <div>
            <Group gap="xs" mb={4}>
              <Title order={4}>Push to Duvo sandbox</Title>
              <Badge color="indigo" variant="light">
                {payload.length} GR{payload.length === 1 ? "" : "s"}
                {totalCount && totalCount !== payload.length
                  ? ` of ${totalCount} reviewed`
                  : ""}
              </Badge>
            </Group>
            <Text size="sm" c="dimmed">
              Without "Run &amp; send": uploads all GRs into one sandbox under{" "}
              <Code>/workspace</Code>, no run is started. With "Run &amp; send":
              uploads all GR JSON files into one sandbox and starts one Duvo
              run for the bundle.
            </Text>
          </div>
          <Group>
            <Button
              variant="default"
              leftSection={<IconArrowLeft size={16} />}
              onClick={onBack}
            >
              Back
            </Button>
          </Group>
        </Group>
      </Paper>

      <Paper>
        <Group justify="space-between" align="flex-start">
          <Stack gap={4}>
            <Group gap="xs">
              <ThemeIcon size="sm" variant="light">
                <IconShieldCheck size={14} />
              </ThemeIcon>
              <Title order={5}>Duvo credentials</Title>
            </Group>
            <Text size="sm" c="dimmed">
              Loaded from Module Configuration → GR Invoice Automation.
            </Text>
          </Stack>
          {configLoading ? (
            <Loader size="sm" />
          ) : (
            <Group gap="xs">
              <Badge
                color={configStatus?.apiKey ? "green" : "red"}
                variant="light"
                leftSection={<IconKey size={12} />}
              >
                apiKey{" "}
                {configStatus?.apiKey
                  ? configStatus.apiKeyMasked
                  : "missing"}
              </Badge>
              <Badge
                color={configStatus?.assignmentID ? "green" : "red"}
                variant="light"
              >
                assignmentID{" "}
                {configStatus?.assignmentID
                  ? configStatus.assignmentIdValue
                  : "missing"}
              </Badge>
            </Group>
          )}
        </Group>

        {!configLoading &&
          (!configStatus?.apiKey || !configStatus?.assignmentID) && (
            <Alert
              mt="md"
              color="yellow"
              variant="light"
              icon={<IconAlertTriangle size={16} />}
            >
              Duvo credentials are not fully configured. Set them in{" "}
              <Anchor component={Link} to="/admin/module-configs">
                Admin → Module Configuration
              </Anchor>{" "}
              before pushing.
            </Alert>
          )}

        <Divider my="md" />

        <Group justify="space-between" align="center" wrap="wrap">
          <Stack gap={4}>
            <Text size="sm" c="dimmed">
              Each GR becomes a file named after its{" "}
              <Code>transaction_key</Code>.
            </Text>
            <Tooltip
              label="Creates one sandbox, uploads all selected GR JSON files, then calls POST /runs once against the configured assignment."
              withArrow
              position="top-start"
              multiline
              w={320}
            >
              <Checkbox
                checked={startRun}
                onChange={(e) => setStartRun(e.currentTarget.checked)}
                disabled={!canPush || pushMut.isPending}
                label={
                  <Group gap={6}>
                    <IconPlayerPlay size={14} />
                    <Text size="sm">
                      Run &amp; send (one sandbox + one bundled run)
                    </Text>
                  </Group>
                }
              />
            </Tooltip>
          </Stack>
          <Button
            leftSection={<IconCloudUpload size={16} />}
            disabled={!canPush}
            loading={pushMut.isPending}
            onClick={() => pushMut.mutate()}
          >
            {startRun ? "Push & run" : "Push"} {payload.length} GR
            {payload.length === 1 ? "" : "s"}
          </Button>
        </Group>
      </Paper>

      {pushError && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />}>
          {pushError}
        </Alert>
      )}

      {pushResult && (
        <Paper>
          <Group justify="space-between" align="flex-start" wrap="wrap">
            <Stack gap={4}>
              <Group gap="xs">
                <ThemeIcon
                  size="sm"
                  color={pushResult.errors || pushResult.run_error ? "yellow" : "green"}
                  variant="light"
                >
                  <IconCheck size={14} />
                </ThemeIcon>
                <Title order={5}>
                  {pushResult.errors || pushResult.run_error
                    ? "Uploaded with partial errors"
                    : pushResult.run_id
                    ? "Uploaded and run started"
                    : "Uploaded successfully"}
                </Title>
              </Group>
              <Text size="sm" c="dimmed">
                Sandbox <Code>{pushResult.sandbox_id}</Code>
                {pushResult.expires_at &&
                  ` · expires ${new Date(pushResult.expires_at).toLocaleString()}`}
              </Text>
              {pushResult.run_id && (
                <Group gap="xs">
                  <Badge
                    color="blue"
                    variant="light"
                    leftSection={<IconPlayerPlay size={12} />}
                  >
                    run {pushResult.run_id}
                  </Badge>
                  {pushResult.run_status && (
                    <Badge color="grape" variant="light">
                      status: {pushResult.run_status}
                    </Badge>
                  )}
                </Group>
              )}
              {pushResult.run_error && (
                <Alert
                  mt={4}
                  color="red"
                  variant="light"
                  icon={<IconAlertTriangle size={14} />}
                >
                  Run failed to start: {pushResult.run_error}
                </Alert>
              )}
            </Stack>
            <Group>
              <Button
                variant="default"
                leftSection={<IconRefresh size={16} />}
                onClick={() => verifyMut.mutate()}
                loading={verifyMut.isPending}
              >
                Verify in sandbox
              </Button>
              <Button
                rightSection={<IconArrowRight size={16} />}
                onClick={onReset}
                variant="light"
              >
                Start new batch
              </Button>
            </Group>
          </Group>

          <Divider my="md" />

          <Stack gap={4}>
            <Text size="sm" fw={500}>
              Uploaded files ({pushResult.files?.length ?? 0})
            </Text>
            <List
              size="sm"
              spacing={4}
              icon={
                <ThemeIcon color="green" size="xs" variant="light">
                  <IconFile size={10} />
                </ThemeIcon>
              }
            >
              {(pushResult.files || []).map((f) => (
                <List.Item key={f.path}>
                  <Code fz="xs">{f.path}</Code>{" "}
                  <Text component="span" size="xs" c="dimmed">
                    · {Number(f.size_bytes || 0).toLocaleString()} bytes
                  </Text>
                </List.Item>
              ))}
            </List>
          </Stack>

          {pushResult.errors && pushResult.errors.length > 0 && (
            <Alert
              mt="md"
              color="red"
              variant="light"
              icon={<IconCircleX size={16} />}
            >
              <Stack gap={4}>
                <Text size="sm" fw={500}>
                  {pushResult.errors.length} file
                  {pushResult.errors.length === 1 ? "" : "s"} failed
                </Text>
                <List size="xs">
                  {pushResult.errors.map((e) => (
                    <List.Item key={e.transaction_key}>
                      <Code fz="xs">{e.filename}</Code>: {e.error}
                    </List.Item>
                  ))}
                </List>
              </Stack>
            </Alert>
          )}

          {sandboxFiles && (
            <>
              <Divider my="md" />
              {sandboxFiles.error ? (
                <Alert color="red" variant="light">
                  {sandboxFiles.error}
                </Alert>
              ) : (
                <Stack gap={4}>
                  <Text size="sm" fw={500}>
                    Current sandbox contents
                  </Text>
                  {sandboxFiles.length === 0 ? (
                    <Text size="sm" c="dimmed">
                      (empty)
                    </Text>
                  ) : (
                    <List size="sm" spacing={2}>
                      {sandboxFiles.map((f) => (
                        <List.Item key={f.path}>
                          <Code fz="xs">{f.path}</Code>{" "}
                          <Text component="span" size="xs" c="dimmed">
                            · {f.type} ·{" "}
                            {Number(f.size_bytes || 0).toLocaleString()} bytes
                          </Text>
                        </List.Item>
                      ))}
                    </List>
                  )}
                </Stack>
              )}
            </>
          )}
        </Paper>
      )}
    </Stack>
  );
}
